import { useEffect, useState } from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Plus, Save, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { SettingsService } from "@/services/settings-service"

import { useAddTemplateMutation } from "../hooks/use-add-template-mutation"
import { useDeleteTemplateMutation } from "../hooks/use-delete-template-mutation"
import { useTemplatesQuery } from "../hooks/use-templates-query"

const createTemplateFormSchema = (t: (key: string) => string) =>
  z.object({
    title: z
      .string()
      .min(1, t("pages.ai_parser.validation.template_title_required"))
      .max(100, t("pages.ai_parser.validation.template_title_max_length")),
  })

type SaveTemplateDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

function CreateNewTemplateItem() {
  const { t } = useTranslation()

  return (
    <SelectItem value="create-new">
      <div className="flex items-center gap-2">
        <Plus className="size-4" />
        {t("pages.ai_parser.add_template_dialog.create_new_template")}
      </div>
    </SelectItem>
  )
}

export function SaveTemplateDialog({ isOpen, onOpenChange }: SaveTemplateDialogProps) {
  const { t } = useTranslation()
  const settingsService = SettingsService.getInstance()
  const userPolicy = settingsService.settings.user_policy

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("")
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [isCompanyTemplate, setIsCompanyTemplate] = useState(false)

  const templatesQuery = useTemplatesQuery()
  const addTemplateMutation = useAddTemplateMutation()
  const deleteTemplateMutation = useDeleteTemplateMutation()

  const templates = templatesQuery.data || []
  const personalTemplates = templates.filter((t) => t.type === "personal")
  const companyTemplates = templates.filter((t) => t.type === "company")

  const templateFormSchema = createTemplateFormSchema(t)
  type TemplateFormValues = z.infer<typeof templateFormSchema>

  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: {
      title: "",
    },
  })

  useEffect(() => {
    if (isOpen) {
      setSelectedTemplateId("create-new")
      setIsCompanyTemplate(false)
      form.reset({ title: "" })
      setIsCreatingNew(true)
    }
  }, [isOpen, form])

  const handleTemplateSelection = (value: string) => {
    const previousTemplateId = selectedTemplateId
    setSelectedTemplateId(value)

    if (value === "create-new") {
      setIsCreatingNew(true)
      setIsCompanyTemplate(false)
      form.reset({ title: "" })
    } else {
      setIsCreatingNew(false)
      const selectedTemplate = templates.find((t) => t.id === value)
      if (selectedTemplate) {
        if (value !== previousTemplateId) {
          setIsCompanyTemplate(selectedTemplate.type === "company")
        }
        form.reset({ title: selectedTemplate.title })
      }
    }
  }

  const handleSubmit = (data: TemplateFormValues) => {
    const templateType = isCompanyTemplate ? "company" : "personal"

    addTemplateMutation.mutate(
      {
        title: data.title,
        type: templateType,
        templateId: isCreatingNew ? undefined : selectedTemplateId,
      },
      {
        onSuccess: () => {
          form.reset()
          setIsCompanyTemplate(false)
          setSelectedTemplateId("create-new")
          setIsCreatingNew(true)
        },
      }
    )
  }

  const handleDelete = () => {
    const selectedTemplate = templates.find((t) => t.id === selectedTemplateId)
    if (!selectedTemplate) {
      return
    }

    deleteTemplateMutation.mutate(
      {
        templateId: selectedTemplateId,
        type: selectedTemplate.type,
      },
      {
        onSuccess: () => {
          setSelectedTemplateId("create-new")
          setIsCreatingNew(true)
          setIsCompanyTemplate(false)
          form.reset({ title: "" })
        },
      }
    )
  }

  const handleCancel = () => {
    form.reset()
    setIsCompanyTemplate(false)
    setSelectedTemplateId("create-new")
    setIsCreatingNew(true)
    onOpenChange(false)
  }

  const isAnyMutationPending =
    addTemplateMutation.isPending || deleteTemplateMutation.isPending || templatesQuery.isPending
  const canDelete = !isCreatingNew && selectedTemplateId !== "create-new"

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t("pages.ai_parser.add_template_dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("pages.ai_parser.add_template_dialog.description")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="flex max-w-full flex-col gap-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {/* Template Selection Dropdown */}
            <div className="flex max-w-full flex-col gap-2">
              <Label htmlFor="template-select">
                {t("pages.ai_parser.add_template_dialog.select_template")}
              </Label>
              <Select
                disabled={isAnyMutationPending}
                value={selectedTemplateId}
                onValueChange={handleTemplateSelection}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      templatesQuery.isPending
                        ? t("pages.ai_parser.add_template_dialog.loading_templates")
                        : t("pages.ai_parser.add_template_dialog.choose_template_or_create_new")
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <CreateNewTemplateItem />
                  {personalTemplates.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>{t("pages.ai_parser.add_template_dialog.personal")}</SelectLabel>
                      {personalTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.title}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {companyTemplates.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>{t("pages.ai_parser.add_template_dialog.company")}</SelectLabel>
                      {companyTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.title}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Title Input */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("pages.ai_parser.add_template_dialog.field_title")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="w-full"
                      disabled={isAnyMutationPending}
                      placeholder={t("pages.ai_parser.add_template_dialog.title_placeholder")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Admin Toggle for Company Template */}
            {userPolicy.is_parser_admin && (
              <div className="flex items-center justify-between rounded-md border p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">
                    {t("pages.ai_parser.add_template_dialog.company_template_label")}
                  </Label>
                  <div className="text-muted-foreground text-sm">
                    {t("pages.ai_parser.add_template_dialog.company_template_description")}
                  </div>
                </div>
                <Switch
                  checked={isCompanyTemplate}
                  disabled={isAnyMutationPending}
                  onCheckedChange={setIsCompanyTemplate}
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-4">
              <div className="flex gap-2">
                <Button
                  disabled={isAnyMutationPending}
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  {t("pages.ai_parser.add_template_dialog.cancel")}
                </Button>
                {canDelete && (
                  <Button
                    disabled={isAnyMutationPending}
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                  >
                    {deleteTemplateMutation.isPending ? (
                      <>
                        <Spinner />
                        {t("pages.ai_parser.add_template_dialog.deleting")}
                      </>
                    ) : (
                      <>
                        <Trash2 className="size-4" />
                        {t("pages.ai_parser.add_template_dialog.delete")}
                      </>
                    )}
                  </Button>
                )}
              </div>
              <Button disabled={isAnyMutationPending || !form.formState.isValid} type="submit">
                {addTemplateMutation.isPending ? (
                  <>
                    <Spinner />
                    {t("pages.ai_parser.add_template_dialog.saving")}
                  </>
                ) : (
                  <>
                    <Save className="size-4" />
                    {t("pages.ai_parser.add_template_dialog.save")}
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
